import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";

import { apiRequest } from "@mhj/shared/api";
import { useAuth } from "@mhj/shared/auth";
import { Card, Field, buttonClass, inputClass, secondaryButtonClass } from "../lib/ui";

export default function VerifyPhone() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const phone = params.get("phone") ?? user?.phone ?? "";

  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiRequest("/auth/phone/verify", {
        method: "POST",
        data: { phone, code: code.trim() },
      });
      await refresh();
      toast.success("Phone number confirmed");
      navigate("/apply");
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      await apiRequest("/auth/phone/request-otp", { method: "POST", data: { phone } });
      toast.success("We sent another code");
      // Matches the server's limit of three per hour, so the button cannot be
      // hammered into a rate-limit error.
      setCooldown(60);
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <Card
      title="Confirm your phone number"
      description={`We sent a 6-digit code by text message to ${phone || "your phone"}. It expires in 10 minutes.`}
    >
      <form className="space-y-4" onSubmit={verify}>
        <Field label="Code" required>
          <input
            className={`${inputClass} text-center text-2xl tracking-[0.4em]`}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="000000"
            maxLength={6}
          />
        </Field>
        <button type="submit" className={buttonClass} disabled={busy || code.length < 6}>
          {busy ? "Checking…" : "Confirm"}
        </button>
        <button
          type="button"
          onClick={resend}
          disabled={cooldown > 0}
          className={secondaryButtonClass}
        >
          {cooldown > 0 ? `Send again in ${cooldown}s` : "Send the code again"}
        </button>
      </form>
    </Card>
  );
}
